import { FileText, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { getEpisodeProgress } from '@/api/api'
import { SegmentResponse } from '@/api/type'
import { Header } from '@/components/shared/header'
import { LoadingSpinner } from '@/components/shared/loading-spinner'
import { Button } from '@/components/ui/button'
import { ActionSection } from '@/components/ui/custom-dropdown'
import { Spinner } from '@/components/ui/spinner'
import { HEADER_HEIGHT, SILENCE_TEXT } from '@/lib/constants'
import { useProgressStore } from '@/store/useProgressStore'
import { PLAYBACK_MODES, PlaybackMode, SHOW_TEXT_MODES, ShowTextMode } from '@/types/playback'

import { FlashcardRegisterSheet } from './components/flashcard-register-sheet'
import { PlaybackControls } from './components/playback-controls/playback-controls'
import { RecordingsSheet } from './components/recordings-sheet/recordings-sheet'
import { SettingsDialog } from './components/settings-control'
import { TranscriptionDisplay } from './components/transcription-display'
import { useAdaptiveAudio } from './hooks/useAdaptiveAudio'
import { useAutoPause } from './hooks/useAutoPause'
import { useRecording } from './hooks/useRecording'
import { useTranscribe } from './hooks/useTranscribe'
import { useTranslation } from './hooks/useTranslation'

const PROGRESS_SAVE_DEBOUNCE = 2000

type EnglishPageProps = {
  onClose: () => void
  baseUrl: string
  fileName: string
  title?: string
  domain: string
  productId: string
  episodeId: string
}

export const EnglishPage = ({ onClose, baseUrl, domain, productId, episodeId }: EnglishPageProps) => {
  const numericEpisodeId = useMemo(() => parseInt(episodeId, 10), [episodeId])
  const lastSavedProgressRef = useRef(0)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [pendingSeek, setPendingSeek] = useState<number | null>(null)
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number | null>(null)
  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>(PLAYBACK_MODES.ONCE)
  const [showText, setShowText] = useState<ShowTextMode>(SHOW_TEXT_MODES.FULL)
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0)
  const [autoPauseEnabled, setAutoPauseEnabled] = useState<boolean>(false)
  const [autoPauseMultiplier, setAutoPauseMultiplier] = useState<number>(1.0)
  const [flashcardSegmentIndex, setFlashcardSegmentIndex] = useState<number | null>(null)
  const [currentWordTime, setCurrentWordTime] = useState<number>(0)
  const [displayOffset, setDisplayOffset] = useState<number>(0)
  const [showSettingsDialog, setShowSettingsDialog] = useState<boolean>(false)

  const navigate = useNavigate()

  // 音声認識フック（audioUrlとtranscriptionも管理）
  // audioUrl: Data URL / Blob URL（認証不要で直接再生可能）
  // html5AudioUrl: バックエンドAPIのネットワーク URL（Authorization ヘッダー必須のため <audio src> 直접指定不可）
  const {
    handleTranscribe,
    isTranscribing,
    audioUrl,
    html5AudioUrl,
    transcription,
    setTranscription,
    isLoadingFromDb,
  } = useTranscribe(domain, productId, episodeId, 0, baseUrl)

  const {
    isReady,
    isPlaying,
    duration,
    currentTime,
    play,
    pause,
    seek,
    switchAudioMode,
    html5AudioRef,
    html5EventHandlers,
    initAudioContext,
  } = useAdaptiveAudio(audioUrl, playbackSpeed)

  // カスタムフック: 自動停止機能
  const { isPausedByAuto, autoPauseProgress, autoPauseTotal, cancelAutoPause, handleAutoPauseCheck } = useAutoPause({
    enabled: autoPauseEnabled,
    multiplier: autoPauseMultiplier,
    isPlaying,
    transcription,
    playbackMode,
    setCurrentSegmentIndex,
    pause,
    play,
  })

  // カスタムフック: 録音機能
  const {
    isRecording,
    recordingTime,
    recordings,
    showRecordingsSheet,
    setShowRecordingsSheet,
    handleRecordClick,
    loadRecordings,
  } = useRecording({
    domain,
    productId,
    episodeId,
  })

  // カスタムフック: 翻訳機能
  const {
    translatedTexts,
    translatingSegmentIndex,
    showTranslationDialog,
    translationDialogSegmentIndex,
    fullTextTranslation,
    isTranslatingFullText,
    showFullTextTranslation,
    handleTranslateSegment,
    closeTranslationDialog,
    handleTranslateFullText,
  } = useTranslation({ transcription })

  const animationFrameId = useRef<number | null>(null)
  const lastTimeUpdate = useRef<number>(0)
  const isUserSeekingRef = useRef<boolean>(false)
  const userSeekTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentSegmentIndexRef = useRef<number | null>(null)

  // ユーザー操作によるセグメント移動時にhandleTimeUpdateの上書きを一時抑制
  const markUserSeeking = useCallback(() => {
    isUserSeekingRef.current = true
    if (userSeekTimeoutRef.current) {
      clearTimeout(userSeekTimeoutRef.current)
    }
    userSeekTimeoutRef.current = setTimeout(() => {
      isUserSeekingRef.current = false
      userSeekTimeoutRef.current = null
    }, 200)
  }, [])

  // 再生速度を変更
  const changePlaybackSpeed = useCallback(
    (speed: number) => {
      setPlaybackSpeed(speed)
      switchAudioMode(speed)
    },
    [switchAudioMode],
  )

  // requestAnimationFrameで滞らかに現在時刻を更新（表示用に0.5秒早める）
  useEffect(() => {
    const updateCurrentTime = () => {
      if (isPlaying && !isPausedByAuto) {
        // 表示用にSEGMENT_DISPLAY_OFFSET秒早める（統一されたcurrentTimeを使用）
        setCurrentWordTime(currentTime + displayOffset)
      }
      animationFrameId.current = requestAnimationFrame(updateCurrentTime)
    }

    if (isPlaying && !isPausedByAuto) {
      animationFrameId.current = requestAnimationFrame(updateCurrentTime)
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
      }
    }
  }, [isPlaying, isPausedByAuto, currentTime, displayOffset])

  // 再生/一時停止トグル
  const togglePlay = useCallback(() => {
    // 自動一時停止中の場合はキャンセル
    if (isPausedByAuto) {
      cancelAutoPause()
    }

    if (isPlaying) {
      pause()
      return
    }

    play()
  }, [isPausedByAuto, cancelAutoPause, isPlaying, pause, play])

  // 指定秒数スキップ
  const skipTime = useCallback(
    (seconds: number) => {
      const newTime = Math.max(0, Math.min(duration, currentTime + seconds))
      seek(newTime)
    },
    [duration, currentTime, seek],
  )

  // 最初の非スキップセグメントのインデックスを取得（DRY原則）
  const getFirstNonSkippedIndex = useCallback(() => {
    if (!transcription) return -1
    return transcription.segments.findIndex((seg) => !seg.skipped)
  }, [transcription])

  // 最初の非スキップセグメントにシークまたは0にシーク
  const seekToFirstNonSkipped = useCallback(() => {
    const firstIndex = getFirstNonSkippedIndex()
    if (firstIndex >= 0 && transcription) {
      seek(transcription.segments[firstIndex].start)
    } else {
      seek(0)
    }
  }, [getFirstNonSkippedIndex, transcription, seek])

  // ストレージ管理ページへ遷移
  const handleOpenStorageManagement = useCallback(() => {
    navigate('/storage-management')
  }, [navigate])

  // セグメントをクリックして再生位置をジャンプ
  const handleSegmentClick = useCallback(
    (segment: SegmentResponse, index: number) => {
      if (!segment.skipped && transcription) {
        // 自動停止中の場合はキャンセル
        if (isPausedByAuto) {
          cancelAutoPause()
        }

        // 連打時のレース条件を防止: handleTimeUpdateによるcurrentSegmentIndex上書きを抑制
        markUserSeeking()

        setCurrentSegmentIndex(index)
        currentSegmentIndexRef.current = index

        // 常にセグメントの最初にジャンプ（再生中のセグメントをタップしても最初から再生）
        seek(segment.start)
        // 停止中の場合のみplay()を呼ぶ（seek()は再生中なら自動的にplay(newTime)を呼ぶ）
        if (!isPlaying) {
          play()
        }
      }
    },
    [isPausedByAuto, cancelAutoPause, isPlaying, play, seek, transcription, markUserSeeking],
  )

  // 単語をクリックして再生位置をジャンプ
  const handleWordClick = useCallback(
    (word: { word: string; start: number; end: number }, segmentIndex: number, e: React.MouseEvent) => {
      e.stopPropagation() // セグメントのクリックイベントを防ぐ
      if (transcription && !transcription.segments[segmentIndex]?.skipped) {
        // 自動停止中の場合はキャンセル
        if (isPausedByAuto) {
          cancelAutoPause()
        }

        // 連打時のレース条件を防止
        markUserSeeking()

        // セグメントインデックスも更新
        setCurrentSegmentIndex(segmentIndex)
        currentSegmentIndexRef.current = segmentIndex
        seek(word.start)
        // seek()は再生中なら自動的にplay(newTime)を呼ぶため、ここでのplay()は不要
        if (!isPlaying) {
          play()
        }
      }
    },
    [transcription, isPausedByAuto, cancelAutoPause, isPlaying, play, seek, markUserSeeking],
  )

  // 再生位置を監視してハイライト更新と自動進行
  const handleTimeUpdate = useCallback(() => {
    if (!transcription) return
    const audioCurrentTime = currentTime

    // 表示用: 0.5秒早く次のセグメントを表示
    const displaySegmentIndex = transcription.segments.findIndex(
      (segment) => segment.start - displayOffset <= audioCurrentTime && audioCurrentTime < segment.end - displayOffset,
    )

    // 実際の再生位置（スキップ判定などに使用）
    const actualSegmentIndex = transcription.segments.findIndex(
      (segment) => segment.start <= audioCurrentTime && audioCurrentTime < segment.end,
    )

    // 現在のセグメントがスキップされている場合、または無音で直前の文セグメントがスキップの場合はスキップ
    const currentSegment = actualSegmentIndex >= 0 ? transcription.segments[actualSegmentIndex] : null

    // Web Audio API用: 再生終了の検出とループ処理
    if (audioCurrentTime >= duration - 0.1 && !isPlaying) {
      // 再生終了時の処理
      if (playbackMode === PLAYBACK_MODES.ALL) {
        // ALLモード: 最初からループ
        seekToFirstNonSkipped()
        play()
        return
      }

      if (playbackMode === PLAYBACK_MODES.ONCE) {
        // ONCEモード: 最初に戻して停止
        seekToFirstNonSkipped()
      }
    }
    let shouldSkip = false

    if (currentSegment?.skipped) {
      // 通常のスキップ
      shouldSkip = true
    }

    if (!shouldSkip && currentSegment?.text === SILENCE_TEXT && actualSegmentIndex > 0) {
      // 無音の場合、直前の文セグメント(無音以外)がスキップされているかチェック
      for (let i = actualSegmentIndex - 1; i >= 0; i--) {
        const prevSegment = transcription.segments[i]
        if (prevSegment.text !== SILENCE_TEXT) {
          // 最初に見つかった文セグメントがスキップなら、この無音もスキップ
          if (prevSegment.skipped) {
            shouldSkip = true
          }
          break
        }
      }
    }

    if (shouldSkip) {
      // 次の非スキップセグメントを探す
      let nextNonSkippedIndex = -1
      for (let i = actualSegmentIndex + 1; i < transcription.segments.length; i++) {
        if (!transcription.segments[i].skipped) {
          nextNonSkippedIndex = i
          break
        }
      }

      if (nextNonSkippedIndex >= 0) {
        // 次の非スキップセグメントにジャンプ
        seek(transcription.segments[nextNonSkippedIndex].start)
        return
      }

      // 全てスキップされている場合（最後まで到達）
      if (playbackMode === PLAYBACK_MODES.ALL) {
        // ALLモードでは最初の非スキップセグメントに戻る
        seekToFirstNonSkipped()
        return
      }

      // ONCE/SENTENCEモードでは停止
      pause()
      return
    }

    // 自動一時停止機能
    const autoPauseTriggered = handleAutoPauseCheck(lastTimeUpdate.current, audioCurrentTime)
    if (autoPauseTriggered) {
      lastTimeUpdate.current = audioCurrentTime
      return
    }

    // 現在の時刻を記録（次回の比較用）
    lastTimeUpdate.current = audioCurrentTime

    // ALL/ONCEモードの場合は自動的にcurrentSegmentIndexを更新（表示用のインデックスを使用）
    // ただし、自動停止中または自動停止が有効な場合は更新しない（自動停止終了時に更新）
    // ユーザーがセグメントをクリック中（連打対策）は上書きしない
    if (
      (playbackMode === PLAYBACK_MODES.ALL || playbackMode === PLAYBACK_MODES.ONCE) &&
      !isPausedByAuto &&
      !autoPauseEnabled &&
      !isUserSeekingRef.current
    ) {
      const newIndex = displaySegmentIndex >= 0 ? displaySegmentIndex : currentSegmentIndexRef.current
      setCurrentSegmentIndex(newIndex)
      currentSegmentIndexRef.current = newIndex
    }

    // 再生モードに応じた自動進行（refを使用して依存配列から除外）
    const segIdx = currentSegmentIndexRef.current
    if (playbackMode === PLAYBACK_MODES.SENTENCE && segIdx !== null) {
      const currentSegment = transcription.segments[segIdx]
      // セグメントの終了時刻に達したら同じセグメントの開始位置に戻る
      if (currentSegment && audioCurrentTime >= currentSegment.end - 0.05) {
        seek(currentSegment.start)
      }
    }
    // ONCEモードでは何もせず、全てのセグメントを最後まで再生
  }, [
    transcription,
    handleAutoPauseCheck,
    isPausedByAuto,
    isPlaying,
    autoPauseEnabled,
    playbackMode,
    duration,
    currentTime,
    seek,
    pause,
    play,
    seekToFirstNonSkipped,
    displayOffset,
  ])

  // 進捗復元: APIから保存済み位置を取得し pendingSeek にセット
  useEffect(() => {
    if (!numericEpisodeId) return
    setPendingSeek(null)
    getEpisodeProgress(numericEpisodeId)
      .then((progress) => {
        if (progress && progress.progress > 0 && !progress.is_completed) {
          setPendingSeek(progress.progress)
        }
      })
      .catch(() => {})
  }, [numericEpisodeId])

  // pendingSeek と isReady+duration の両方が揃ったらシーク実行
  useEffect(() => {
    if (pendingSeek === null || !isReady || duration <= 0) return
    seek(pendingSeek)
    lastSavedProgressRef.current = pendingSeek

    // 進捗位置に対応するセグメントをハイライト
    if (transcription) {
      const segmentIndex = transcription.segments.findIndex(
        (segment) => segment.start <= pendingSeek && pendingSeek < segment.end,
      )
      if (segmentIndex >= 0) {
        setCurrentSegmentIndex(segmentIndex)
        currentSegmentIndexRef.current = segmentIndex
      }
    }

    setPendingSeek(null)
  }, [pendingSeek, isReady, duration, seek, transcription])

  // 進捗保存（即時 — 閉じるとき・再生完了時に使用）
  const saveProgress = useCallback(
    (options?: { incrementPlayCount?: boolean }) => {
      if (!numericEpisodeId || !duration || duration <= 0) return
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      const progress = Math.floor(currentTime)
      const isCompleted = currentTime >= duration - 1
      // useProgressStore 経由で保存 → ProductContainer のUI同期も行われる
      useProgressStore
        .getState()
        .saveEpisodeProgress(numericEpisodeId, progress, isCompleted, options?.incrementPlayCount)
    },
    [numericEpisodeId, currentTime, duration],
  )

  // 進捗保存（デバウンス — 再生中に定期保存）
  const debouncedSaveProgress = useCallback(() => {
    if (!numericEpisodeId || !duration || duration <= 0) return
    const diff = Math.abs(currentTime - lastSavedProgressRef.current)
    if (diff < 5) return
    if (saveTimeoutRef.current) return

    saveTimeoutRef.current = setTimeout(() => {
      lastSavedProgressRef.current = currentTime
      const progress = Math.floor(currentTime)
      const isCompleted = currentTime >= duration - 1
      useProgressStore.getState().saveEpisodeProgress(numericEpisodeId, progress, isCompleted)
      saveTimeoutRef.current = null
    }, PROGRESS_SAVE_DEBOUNCE)
  }, [numericEpisodeId, currentTime, duration])

  // currentTimeが変化したらhandleTimeUpdateを呼ぶ + 進捗保存
  useEffect(() => {
    if (isPlaying && !isPausedByAuto) {
      handleTimeUpdate()
      debouncedSaveProgress()
    }
  }, [currentTime, isPlaying, isPausedByAuto, handleTimeUpdate, debouncedSaveProgress])

  // 再生終了時に完了として保存
  useEffect(() => {
    if (!isPlaying && duration > 0 && currentTime >= duration - 1) {
      saveProgress({ incrementPlayCount: true })
    }
  }, [isPlaying, currentTime, duration, saveProgress])

  // unmount時に未保存の進捗をフラッシュ
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      if (userSeekTimeoutRef.current) {
        clearTimeout(userSeekTimeoutRef.current)
      }
    }
  }, [])

  // 閉じるときに進捗を保存してから閉じる
  const handleClose = useCallback(() => {
    saveProgress()
    onClose()
  }, [saveProgress, onClose])

  const dropdownSections: ActionSection[] = useMemo(
    () => [
      {
        type: 'action' as const,
        label: 'Actions',
        items: [
          {
            key: 'settings',
            label: '設定',
            onClick: () => setShowSettingsDialog(true),
          },
          {
            key: 'transcribe',
            label: 'テキスト初期化',
            onClick: handleTranscribe,
          },
          {
            key: 'storage',
            label: '容量管理',
            onClick: handleOpenStorageManagement,
          },
          {
            key: 'record',
            label: '録音管理',
            onClick: () => setShowRecordingsSheet(true),
          },
        ],
      },
    ],
    [handleTranscribe, handleOpenStorageManagement, setShowRecordingsSheet],
  )

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* ヘッダー */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header title={episodeId} onClose={handleClose} dropdownSections={dropdownSections} />
      </div>

      {/* メインコンテンツ */}
      <div
        className="flex-1 overflow-y-auto p-3 pb-56 w-full"
        style={{
          marginTop: HEADER_HEIGHT,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        }}
        onTouchStart={() => {
          // iOS対策: タッチ時にAudioContextを初期化
          // ユーザー操作のコンテキスト内でresumeを開始
          if (initAudioContext) {
            initAudioContext().catch((err) => {
              console.warn('[EnglishPage] Failed to init AudioContext:', err)
            })
          }
        }}
      >
        {/* HTML5 audio要素（常にマウント: 速度変更時に即座に切り替えられるよう事前ロードしておく） */}
        {/* audioUrl（Data URL/Blob URL）を優先: Authorization ヘッダーなしでアクセスできるネットワーク URL は認証エラーになるため */}
        {(audioUrl || html5AudioUrl) && (
          <audio
            ref={html5AudioRef}
            src={audioUrl || html5AudioUrl || undefined}
            {...html5EventHandlers}
            onError={(e) => {
              const result = html5EventHandlers.onError(e)
              if (result.shouldFallback) {
                toast.error('音声の読み込みに失敗しました')
                // エラー時は速度を1.0に戻してWeb Audio APIに切り替え
                setPlaybackSpeed(1.0)
              }
            }}
            style={{ display: 'none' }}
          />
        )}

        {/* 音声再生セクション */}
        {isLoadingFromDb || !isReady ? (
          <LoadingSpinner
            loading={true}
            size={32}
            inCard={true}
            message={isLoadingFromDb ? 'データ読み込み中...' : '音声デコード中...'}
          />
        ) : !transcription ? (
          <div className="bg-card rounded-lg p-6 shadow-md h-full">
            <div className="flex items-center gap-2 mb-4 flex-wrap justify-center h-full">
              <Button onClick={handleTranscribe} disabled={isTranscribing} variant="outline">
                {isTranscribing ? (
                  <>
                    <Spinner size={16} />
                    <span className="ml-2">音声認識中...</span>
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    テキスト生成
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : null}

        {/* 字幕表示セクション */}
        {!isLoadingFromDb && transcription && (
          <TranscriptionDisplay
            transcription={transcription}
            setTranscription={setTranscription}
            showText={showText}
            currentSegmentIndex={currentSegmentIndex}
            currentWordTime={currentWordTime}
            isTranslatingFullText={isTranslatingFullText}
            showFullTextTranslation={showFullTextTranslation}
            fullTextTranslation={fullTextTranslation}
            handleTranslateFullText={handleTranslateFullText}
            handleSegmentClick={handleSegmentClick}
            isPausedByAuto={isPausedByAuto}
            autoPauseTotal={autoPauseTotal}
            autoPauseProgress={autoPauseProgress}
            translatingSegmentIndex={translatingSegmentIndex}
            handleTranslateSegment={handleTranslateSegment}
            handleWordClick={handleWordClick}
            episodeId={numericEpisodeId}
            onFlashcardRegister={(segmentIndex) => setFlashcardSegmentIndex(segmentIndex)}
          />
        )}
      </div>

      {/* 再生コントロール(固定フッター) */}
      <PlaybackControls
        isPlaying={isPlaying}
        playbackSpeed={playbackSpeed}
        currentTime={currentTime}
        duration={duration}
        onSeek={seek}
        onTogglePlay={togglePlay}
        onSkipTime={skipTime}
        onChangeSpeed={changePlaybackSpeed}
        autoPauseEnabled={autoPauseEnabled}
        autoPauseMultiplier={autoPauseMultiplier}
        isPausedByAuto={isPausedByAuto}
        autoPauseProgress={autoPauseProgress}
        autoPauseTotal={autoPauseTotal}
        onAutoPauseEnabledChange={setAutoPauseEnabled}
        onAutoPauseMultiplierChange={setAutoPauseMultiplier}
        playbackMode={playbackMode}
        onPlaybackModeChange={setPlaybackMode}
        showText={showText}
        onShowTextChange={setShowText}
        onRecordClick={handleRecordClick}
        domain={domain}
        productId={productId}
        episodeId={episodeId}
        isRecording={isRecording}
        recordingTime={recordingTime}
      />

      {/* 録音リストSheet */}
      <RecordingsSheet
        open={showRecordingsSheet}
        onOpenChange={setShowRecordingsSheet}
        recordings={recordings}
        onRecordingsUpdate={loadRecordings}
        transcription={transcription}
        referenceAudioUrl={html5AudioUrl || ''}
        referenceWords={transcription?.segments.flatMap((seg) => seg.words)}
        segments={transcription?.segments}
      />

      {/* 設定ダイアログ */}
      <SettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        displayOffset={displayOffset}
        onDisplayOffsetChange={setDisplayOffset}
      />

      {/* 翻訳ダイアログ */}
      {showTranslationDialog && translationDialogSegmentIndex !== null && transcription && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeTranslationDialog}
        >
          <div className="bg-background rounded-lg shadow-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold">翻訳</h3>
              <Button onClick={closeTranslationDialog} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted-foreground mb-2">原文</div>
                <div className="p-3 bg-muted rounded-md">
                  {transcription.segments[translationDialogSegmentIndex].text}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">日本語訳</div>
                <div className="p-3 bg-muted rounded-md">
                  {translatedTexts.get(translationDialogSegmentIndex) || '翻訳がありません'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* フラッシュカード登録シート */}
      {flashcardSegmentIndex !== null && transcription && (
        <FlashcardRegisterSheet
          isOpen={flashcardSegmentIndex !== null}
          onClose={() => setFlashcardSegmentIndex(null)}
          episodeId={numericEpisodeId}
          segmentIndex={flashcardSegmentIndex}
          segments={transcription.segments}
        />
      )}
    </div>
  )
}
